import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Add, Cancel, Engineering, RemoveRedEyeOutlined } from '@mui/icons-material';
import { EnquiryDetail } from '../../services/quoteRequests';
import {
  SubmitExpertAdviceQuotation,
  SubmitSellOldBoilerQuotation,
  SubmitSiteVisitQuotation,
  UpdateEnquiryStatus,
} from '../../services/requests';
import AssignTechnicianModal from '../Booking/actions/TechnicianModal';
import { QuotationModal } from '../../components/common/QuotationModal';
import useSnackbar from '../../hooks/useSnackbar';

// Each request type keeps its own quotation endpoint/model (see
// services/requests.ts) — this just routes to the right one for whichever
// subType the loaded request turns out to be.
const QUOTATION_SUBMITTERS: Record<
  string,
  typeof SubmitSellOldBoilerQuotation
> = {
  SELL_OLD_EQUIPMENT: SubmitSellOldBoilerQuotation,
  OLD_BOILER: SubmitSellOldBoilerQuotation,
  EXPERT_ADVICE: SubmitExpertAdviceQuotation,
  SITE_VISIT: SubmitSiteVisitQuotation,
};

/**
 * Generic detail page for user-app quote requests. Clones the Sell Old AC
 * RequestDetail layout (customer block + per-type sections + assign action).
 * The backend detail endpoint (/admin/enquiry/:id) already joins each
 * request type's satellite document; we render whichever one is populated.
 */

// detail-response field -> section heading
const SECTION_SOURCES: [string, string][] = [
  ['amcDetails', 'AMC Details'],
  ['sellOldEquipmentDetails', 'Equipment Details'],
  ['expertAdviceDetails', 'Expert Advice Details'],
  ['siteVisitDetails', 'Site Visit Details'],
];

const HIDDEN_KEYS = ['_id', 'enquiryId', 'createdAt', 'updatedAt', '__v'];

// camelCase -> "Camel Case"
const labelize = (key: string) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) {
    return value.length ? value.map(formatValue).join(', ') : '-';
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value).filter(
      ([k, v]) => !HIDDEN_KEYS.includes(k) && v !== null && v !== '',
    );
    return entries.length
      ? entries.map(([k, v]) => `${labelize(k)}: ${formatValue(v)}`).join(' | ')
      : '-';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

function QuoteRequestDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  // Accepts either the current { id, returnPath } shape or a bare id string
  // (older callers/back-forward cache entries), so this never breaks on a
  // stale navigation state.
  const requestId =
    location?.state && typeof location.state === 'object'
      ? (location.state as { id?: string }).id
      : (location?.state as string | undefined);
  const [detailValue, setDetailValue] = useState<any | null>(null);
  const [assignModal, setAssignModal] = useState<boolean>(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openQuotationModal, setOpenQuotationModal] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const submitQuotation = detailValue?.subType
    ? QUOTATION_SUBMITTERS[detailValue.subType]
    : undefined;

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  async function fetchDetail() {
    if (!requestId) return;
    await EnquiryDetail(requestId).then((response) => {
      setDetailValue(response.data);
    });
  }

  const isCancelled = detailValue?.status === 'CANCELLED';

  const handleRejectRequest = async () => {
    // Backend resolves the enquiry by its human-readable code, not Mongo _id
    if (!detailValue?.enquiryId) return;

    try {
      setRejecting(true);
      await UpdateEnquiryStatus(detailValue.enquiryId, 'CANCELLED');
      showSnackbar('Request rejected', 'success');
      setOpenRejectDialog(false);
      fetchDetail();
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to reject request',
        'error',
      );
    } finally {
      setRejecting(false);
    }
  };

  // pick the populated per-type section(s)
  const sections = SECTION_SOURCES.map(([key, title]) => {
    const raw = detailValue?.[key];
    const items = (Array.isArray(raw) ? raw : raw ? [raw] : []).filter(
      (item) =>
        item &&
        Object.keys(item).some(
          (k) => !HIDDEN_KEYS.includes(k) && item[k] !== null && item[k] !== '',
        ),
    );
    return { key, title, items };
  }).filter((s) => s.items.length > 0);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p:1
        }}
      >
        <IconButton onClick={() => navigate(-1)} sx={{ color: '#fff' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>
          {detailValue?.subType
            ? `${detailValue.subType.replaceAll('_', ' ')} Request`
            : 'Request Details'}
        </Typography>
      </Box>

      <Divider sx={{ marginBottom: 3 }} />

      <div className="px-5">
        <div className="w-full mx-auto bg-[#1a1a1a] rounded-xl shadow-lg border border-[#2a2a2a]">
          <div className="flex justify-between items-center px-6 py-4 border-b border-[#2a2a2a]">
            <div className="flex items-center gap-2 text-lg font-medium text-white">
              Customer Details
            </div>

            <div className="text-sm text-white">
              Status :
              <span
                className={`font-medium ml-1 ${isCancelled ? 'text-red-500' : 'text-green-500'}`}
              >
                {detailValue?.status}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 px-6 py-6 text-sm bg-[#232323]">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Full Name</p>
                <p className="text-green-500 underline w-full">
                  {detailValue?.user_info?.name || '-'}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Request Type</p>
                <p className="w-full text-white">
                  {detailValue?.subType?.replaceAll('_', ' ') || '-'}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Address</p>
                <p className="w-full text-white">
                  {detailValue?.addressDetails
                    ? [
                        detailValue.addressDetails.house,
                        detailValue.addressDetails.street,
                        detailValue.addressDetails.city,
                        detailValue.addressDetails.state,
                        detailValue.addressDetails.zipcode,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : '-'}
                </p>
              </div>

              {detailValue?.noOfAc > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-gray-400 w-full">Total AC</p>
                  <p className="w-full text-white">{detailValue?.noOfAc}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Contact Number</p>
                <p className="w-full text-white">
                  {detailValue?.user_info?.phoneNumber || '-'}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Requested Date</p>
                <p className="w-full text-white">
                  {detailValue?.schedule?.date
                    ? new Date(detailValue.schedule.date).toLocaleDateString()
                    : detailValue?.createdAt
                      ? new Date(detailValue.createdAt).toLocaleDateString()
                      : '-'}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Slot</p>
                <p className="w-full text-white">
                  {detailValue?.schedule?.slot || '-'}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-gray-400 w-full">Assigned Technician</p>
                {detailValue?.technician_info ? (
                  <p className="w-full text-white flex items-center justify-between">
                    {detailValue?.technician_info?.name}{' '}
                    <RemoveRedEyeOutlined
                      onClick={() => {
                        navigate(
                          `/technicians/${detailValue?.technician_info?._id}`,
                        );
                      }}
                      style={{ color: 'red', cursor: 'pointer' }}
                    />
                  </p>
                ) : (
                  <p className="w-full text-white flex items-center justify-between">
                    -
                  </p>
                )}
              </div>
            </div>
          </div>

          {sections.map((section) =>
            section.items.map((item: any, index: number) => (
              <div key={`${section.key}-${index}`}>
                <div className="border-t border-[#2a2a2a]" />

                <div className="px-6 py-5">
                  <h3 className="text-white text-sm mb-4">
                    {section.title}
                    {section.items.length > 1 ? ` ${index + 1}` : ''}
                  </h3>

                  <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
                    {Object.entries(item)
                      .filter(
                        ([k, v]) =>
                          !HIDDEN_KEYS.includes(k) && v !== null && v !== '',
                      )
                      .map(([k, v]) => (
                        <div
                          key={k}
                          className="flex items-center justify-between"
                        >
                          <p className="text-gray-400 w-full">{labelize(k)}</p>
                          <p className="w-full text-white">{formatValue(v)}</p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )),
          )}
        </div>
        <div className="mt-4 flex justify-between items-center">
          <Button
            variant="outlined"
            sx={{
              color: '#7B1FA2',
              borderColor: '#7B1FA2',
              px: 2.5,
              py: 1.2,
              borderRadius: '14px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#9C27B0',
                backgroundColor: 'rgba(123, 31, 162, 0.12)',
              },
            }}
            startIcon={<Cancel />}
            disabled={!detailValue?.enquiryId || isCancelled}
            onClick={() => setOpenRejectDialog(true)}
          >
            Reject Request
          </Button>

          <div className="flex items-center gap-2">
            {submitQuotation && (
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{
                  backgroundColor: '#7B1FA2',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#5E167A' },
                  px: 2.5,
                  py: 1.2,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                }}
                disabled={!detailValue}
                onClick={() => setOpenQuotationModal(true)}
              >
                Add Quotation
              </Button>
            )}

            {!detailValue?.technician_info ? (
              <Button
                sx={{
                  color: '#fff',
                  backgroundColor: '#7B1FA2',
                  '&:hover': { backgroundColor: '#7B1FA2' },
                  padding: '10px 20px',
                  borderRadius: '14px',
                }}
                startIcon={<Engineering />}
                onClick={() => setAssignModal(true)}
              >
                Assign Technician
              </Button>
            ) : (
              ''
            )}
          </div>
        </div>
      </div>
      {requestId && (
        <AssignTechnicianModal
          isOpen={assignModal}
          onClose={() => setAssignModal(false)}
          bookingId={requestId}
          onReload={fetchDetail}
          isEnquiry={true}
        />
      )}

      {/* Add quotation */}
      {submitQuotation && (
        <QuotationModal
          open={openQuotationModal}
          onClose={() => setOpenQuotationModal(false)}
          contextFields={[
            {
              label: 'Customer',
              value: detailValue?.user_info?.name || 'N/A',
            },
            {
              label: 'Request Type',
              value: detailValue?.subType?.replaceAll('_', ' ') || 'N/A',
            },
          ]}
          onSubmit={(payload) => {
            if (!detailValue?._id) return Promise.reject(new Error('Missing request id'));
            return submitQuotation(detailValue._id, payload).then(() => undefined);
          }}
          onSubmitted={fetchDetail}
        />
      )}

      {/* Reject confirmation */}
      <Dialog
        open={openRejectDialog}
        onClose={() => !rejecting && setOpenRejectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to reject this request? This will mark the
            enquiry as CANCELLED.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenRejectDialog(false)}
            disabled={rejecting}
          >
            Keep Request
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRejectRequest}
            disabled={rejecting}
          >
            {rejecting ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {SnackbarComponent}
    </Box>
  );
}

export default QuoteRequestDetail;
